/* eslint-disable import/no-cycle */
import { compact, get, isArray, groupBy, keyBy, uniq } from 'lodash';
import moment from 'moment';
import {
  userService,
  MS_PHASE_3_ENDPOINTS,
  MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT,
  uacService,
} from 'utils/msEndpointConstants';
import {
  retrieveListOrObject,
  retrieveObjectMapper,
  convertObjToCamelCase,
  convertObjToSnakeCase,
} from 'utils';
import {
  DATE_PICKER_FORMAT,
  DEFAULT_ERROR,
  DEFAULT_ITEMS_PER_PAGE,
  MAX_PER_PAGE,
} from '../utils/constants';
import {
  transformPOCUsers,
  transformFindAllReportingStaff,
  transformLinkpassSn2UserInfoResponse,
  transformRegisterWithLinkPassInfo,
} from '../utils/ms-utils/ms-phase-3/UserUtils';
import DatadogHandler from '../utils/datadog';
import {
  getChildRelationsByUserIds,
  msGetChildRelationFromParentId,
} from './ChildService';
import { getCentreAddressByIds } from './RegistrationsService';

const fileName = 'src/services/UserService.js';
const { USER } = MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT;
const { USER: USER_P3, CENTRE, UAC, STAFF } = MS_PHASE_3_ENDPOINTS;

export const getRoleTemplatesByRoleIds = (roleIds, pagination = {}) => {
  if (!roleIds?.length) return {};
  const filter = {
    fk_role: [{ eq: roleIds }],
  };
  const [endpoint, body] = UAC.getRoleTemplatesByFilter(filter);
  const requestConfig = {};
  if (pagination.page || pagination.perPage) {
    requestConfig.params = {
      ...(pagination.page && { page: pagination.page }),
      ...(pagination.perPage && { perPage: pagination.perPage }),
    };
  }
  return uacService()
    .post(endpoint, body, requestConfig)
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getModuleCategoriesByRoleTemplateIds = async roleTemplateIds => {
  if (!roleTemplateIds?.length) {
    return {
      success: true,
      data: [],
    };
  }
  try {
    const resp = await uacService().get(
      UAC.getModuleCategoriesByRoleTemplateIds(roleTemplateIds)
    );
    return get(resp, 'data', {});
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getModuleCategoriesByRoleTemplateIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      data: [],
      error: get(
        error,
        'response.data.error',
        get(error, 'message', DEFAULT_ERROR)
      ),
    };
  }
};

const enrichRolesWithTemplatesAndCategories = async rolesData => {
  const roleIds = rolesData.map(role => get(role, 'id') || get(role, 'ID'));
  const roleTemplates = await getRoleTemplatesByRoleIds(roleIds);
  const roleTemplatesByRoleId = groupBy(roleTemplates, 'fk_role');
  const roleTemplateIds = roleTemplates.map(t => get(t, 'id') || get(t, 'ID'));
  const moduleCategoriesResult = await getModuleCategoriesByRoleTemplateIds(
    roleTemplateIds
  );

  const moduleCategoryByFkRoleTemplate = Object.keys(
    moduleCategoriesResult
  ).reduce((acc, key) => {
    const categoryData = get(moduleCategoriesResult, `${key}.data`, []);
    if (categoryData.length > 0) {
      acc[key] = categoryData[0];
    }
    return acc;
  }, {});

  return {
    roleTemplatesByRoleId,
    moduleCategoryByFkRoleTemplate,
  };
};

export const msFindAllRoles = async (_, { reqData }) => {
  try {
    const filter = get(reqData, 'filter', {});
    const pagination = get(reqData, 'pagination', null);
    const isStaff = !!get(filter, 'isStaff');

    const payload = convertObjToSnakeCase({
      isStaff,
      ...(pagination && {
        page: get(pagination, 'page', 1),
        perPage: get(pagination, 'perPage', DEFAULT_ITEMS_PER_PAGE),
      }),
    });
    const resp = await uacService().post(UAC.findRoleBySchool, payload);
    const responseData = get(resp, 'data', {});
    const data = get(responseData, 'Data', []);
    const totalCount = get(responseData, 'TotalCount', data.length);

    const { roleTemplatesByRoleId, moduleCategoryByFkRoleTemplate } =
      await enrichRolesWithTemplatesAndCategories(data);

    const roleData = {
      success: true,
      data: {
        findRoleByFkSchool: {
          totalCount,
          data: data.map(role => ({
            ...convertObjToCamelCase(role),
            roleTemplates: {
              data: (roleTemplatesByRoleId[get(role, 'id')] || []).map(
                template => ({
                  ...convertObjToCamelCase(template),
                  moduleCategory: convertObjToCamelCase(
                    moduleCategoryByFkRoleTemplate[get(template, 'id')]
                  ),
                })
              ),
            },
          })),
        },
      },
    };
    return roleData;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFindAllRoles',
        fileName,
      },
      'error'
    );
    return {
      data: {
        findRoleByFkSchool: {
          data: [],
        },
      },
      success: false,
      error: get(
        error,
        'response.data.error',
        get(error, 'message', DEFAULT_ERROR)
      ),
    };
  }
};

export const msFetchAllModuleCategory = async (_, reqData) => {
  try {
    const resp = await uacService().get(UAC.getAllModuleCategory);
    return {
      ...reqData,
      data: {
        findAllModuleCategory: convertObjToCamelCase(get(resp, 'data', {})),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAllModuleCategory ',
        fileName,
      },
      'error'
    );
    return {
      data: {
        findAllModuleCategory: {
          data: [],
        },
      },
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateRole = async (_, reqData) => {
  try {
    const resp = await uacService().put(
      ...UAC.updateRole(get(reqData, 'IDRole'), {
        module_relations: (get(reqData, 'ModuleRelations') || []).map(md =>
          retrieveObjectMapper(md, [
            'module_category_code:ModuleCategoryCode',
            'access:Access',
          ])
        ),
        role_update: {
          fk_school: get(reqData, 'Role.fkSchool'),
          is_staff: get(reqData, 'Role.isStaff'),
          label: get(reqData, 'Role.label'),
          last_modified_user: get(reqData, 'Role.lastModifiedUser'),
          name: get(reqData, 'Role.name'),
        },
      })
    );
    return {
      data: {
        updateRole: get(resp, 'data.success', false),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateRole ',
        fileName,
      },
      'error'
    );
    return {
      data: {
        updateRole: false,
      },
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msAddNewRole = async (_, reqData) => {
  try {
    if (!reqData?.Role?.label?.length) return { data: {}, success: true };

    const body = {
      label: reqData?.Role?.label || '',
      ...(reqData?.Role?.name && {
        name: reqData?.Role?.name,
      }),
      is_staff: reqData?.Role?.isStaff || false,
      module_relation:
        reqData?.ModuleRelations?.map(md => ({
          ...convertObjToSnakeCase(md, [
            'access:Access',
            'module_category_code:ModuleCategoryCode',
          ]),
        })) || [],
    };

    const uacSvc = uacService();
    const resp = await uacSvc.post(...UAC.addRole(body));

    return {
      data: {
        addRole: resp?.data?.success || false,
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddNewRole ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msFindRolesForUserCreation = async (_, reqData) => {
  try {
    const filter = get(reqData, 'reqData.filter', {});
    const isStaff = get(filter, 'isStaff', false);

    const payload = convertObjToSnakeCase({
      isStaff,
    });

    const resp = await uacService().post(UAC.findRoleForUserCreation, payload);

    const responseData = get(resp, 'data', {});
    const data = get(responseData, 'Data', []);
    const totalCount = get(responseData, 'TotalCount', data.length);

    const { roleTemplatesByRoleId, moduleCategoryByFkRoleTemplate } =
      await enrichRolesWithTemplatesAndCategories(data);

    return {
      ...reqData,
      success: true,
      data: {
        findRoleForUserCreation: {
          totalCount,
          data: data.map(role => ({
            ...convertObjToCamelCase(role),
            roleTemplates: {
              data: (roleTemplatesByRoleId[get(role, 'id')] || []).map(
                template => ({
                  ...convertObjToCamelCase(template),
                  moduleCategory: convertObjToCamelCase(
                    moduleCategoryByFkRoleTemplate[get(template, 'id')]
                  ),
                })
              ),
            },
          })),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFindRolesForUserCreation',
        fileName,
      },
      'error'
    );
    return {
      data: {
        findRoleForUserCreation: {
          data: [],
        },
      },
      success: false,
      error: get(
        error,
        'response.data.error',
        get(error, 'message', DEFAULT_ERROR)
      ),
    };
  }
};

export const msUpdateStaff = async (_, reqData) => {
  try {
    const data = await userService().put(
      USER_P3.updateStaff(get(reqData, 'IDStaff')),
      {
        staff_id: get(reqData, 'staff.staffID'),
        centre_ids: get(reqData, 'centres') || [],
        display_name: get(reqData, 'staff.displayName'),
        email: get(reqData, 'staff.email'),
        fk_school: get(reqData, 'staff.fkSchool'),
        fk_staff_reporting_officer: get(
          reqData,
          'staff.fkStaffReportingOfficer'
        ),
        fk_user: get(reqData, 'staff.fkUser'),
        is_resigned: get(reqData, 'staff.isResigned', false),
        last_modified_user: get(reqData, 'staff.lastModifiedUser'),
        phone_country_code: get(reqData, 'staff.phoneCountryCode'),
        phone_number: get(reqData, 'staff.phoneNumber'),
        resigned_effective_at: get(reqData, 'staff.resignedEffectiveAt'),
        role_names: get(reqData, 'roleNames') || [],
        ...(get(reqData, 'loginPin') && {
          login_pin: get(reqData, 'loginPin'),
        }),
      }
    );
    return {
      success: get(data, 'success', false),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateStaff ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msFetchSearchResultsByEmail = async (_, reqData) => {
  try {
    const { email } = reqData;
    const res = await userService().get(USER_P3.getADUserByEmail(email));

    const transformedData = res?.data
      ? convertObjToCamelCase(res.data, [], ['GivenName:given_name', 'ID:id'])
      : null;

    return {
      success: true,
      data: {
        getADUserByEmail: transformedData,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchSearchResultsByEmail',
        fileName,
      },
      'error'
    );
    return {
      data: {
        getADUserByEmail: null,
      },
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchSearchResultsByName = async (_, reqData) => {
  try {
    const { name } = reqData;
    const res = await userService().get(USER_P3.getADUsersByName(name));

    const transformedData = (res?.data?.data || []).map(user =>
      convertObjToCamelCase(user, [], ['GivenName:given_name', 'ID:id'])
    );

    return {
      success: true,
      data: {
        getADUsersByName: transformedData,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchSearchResultsByName',
        fileName,
      },
      'error'
    );
    return {
      data: {
        getADUsersByName: [],
      },
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msAddNewStaff = async (_, reqData) => {
  try {
    const userSvc = userService();
    const resp = await userSvc.post(
      ...STAFF.addNewStaff(
        convertObjToSnakeCase(reqData, [
          'centre_ids:centres',
          'first_name:firstname',
          'last_name:lastname',
          'role_ids:roles',
          'id_school:IDschool',
        ])
      )
    );

    return {
      data: {
        addNewStaff: resp?.data?.success || false,
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddNewStaff ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const getUserByIds = userIds => {
  if (userIds.length === 0) {
    return [];
  }
  return userService()
    .post(...USER.GET_BY_IDS(userIds))
    .then(retrieveListOrObject)
    .catch(() => ({}));
};

export const msFetchReportingStaff = async (_, reqData) => {
  try {
    const params = {
      page: 1,
      perPage: DEFAULT_ITEMS_PER_PAGE,
    };
    const body = {
      school_id: reqData?.schoolID,
      search_string: reqData?.searchString,
    };
    const resp = await userService().post(
      ...USER_P3.findAllReportingStaff(body, params)
    );
    if (get(resp, 'data.totalCount', 0) > 0) {
      const fkUsers = compact(
        uniq(resp?.data?.data?.map(usr => usr?.fk_user) || [])
      );
      let users = [];
      users = await getUserByIds(fkUsers);
      const tansformedData = transformFindAllReportingStaff({
        resp,
        users,
      });
      return {
        data: tansformedData,
        success: true,
      };
    }
    return {
      success: false,
      data: {
        findAllReportingStaff: [],
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchReportingStaff ',
        fileName,
      },
      'error'
    );
    return {
      data: {
        findAllReportingStaff: [],
      },
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getParentByIds = parentIds => {
  if (parentIds.length === 0) {
    return [];
  }
  return userService()
    .post(...USER.GET_PARENT_BY_IDS(parentIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const msFetchAllPOCUser = async (_, reqData) => {
  try {
    const { pagination } = reqData;
    const params = {
      page: pagination?.page || 1,
      perPage: pagination?.perPage || MAX_PER_PAGE,
    };
    const userSvc = userService();
    const resp = await userSvc.get(...USER_P3.getAllPOCUsers(params));
    const pocUsers = resp?.data?.data || [];
    const fkUsers = compact(uniq(pocUsers?.map(usr => usr?.fk_user) || []));

    let users = [];
    if (fkUsers?.length > 0) {
      users = await getUserByIds(fkUsers);
    }

    const data = transformPOCUsers({ data: pocUsers, users });
    return {
      success: true,
      data: {
        findAllPOCUser: {
          totalCount: resp?.data?.totalCount || 0,
          data,
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAllPOCUser ',
        fileName,
      },
      'error'
    );
    return {
      data: {
        updateStaff: null,
      },
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msAddUpdateSpouse = async (_, reqData) => {
  try {
    const body = {
      ...retrieveObjectMapper(reqData, [
        'child_id:childID',
        'is_child_applicant:isChildApplicant',
        'school_id:schoolID',
        'update_parent_id:updateParentID',
        'update_user_id:updateUserID',
      ]),
      applicant: {
        applicant:
          retrieveObjectMapper(get(reqData, 'applicant.applicant', {}), [
            'email',
            'firstname',
            'lastname',
            'mobile_phone:mobilePhone',
            'mobile_phone_country_code:mobilePhoneCountryCode',
            'birthdate',
            'nationality',
            'marital_status:maritalStatus',
            'working_status:workingStatus',
            'identification_no:identificationNo',
            'self_employed_monthly_income:selfEmployedMonthlyIncome',
            'occupational_title:occupationalTitle',
            'is_employed:isEmployed',
            'highest_qualification:highestQualification',
          ]) || {},
        applicant_address:
          retrieveObjectMapper(
            get(reqData, 'applicant.applicantAddress', {}),
            []
          ) || {},
        applicant_detail:
          retrieveObjectMapper(get(reqData, 'applicant.applicantDetail', {}), [
            'is_applicant_spouse:isApplicantSpouse',
            'email',
            'firstname',
            'lastname',
            'mobile_phone:mobilePhone',
            'mobile_phone_country_code:mobilePhoneCountryCode',
            'birthdate',
          ]) || {},
        applicant_relationship_to_child:
          retrieveObjectMapper(
            get(reqData, 'applicant.applicantRelationshipToChild', {}),
            ['fk_child:fkChild', 'fk_relation:fkRelation']
          ) || {},
      },
    };
    const resp = await userService().post(...USER_P3.addUpdateSpouse(body));
    return {
      success: true,
      data: {
        addUpdateSpouse: retrieveObjectMapper(get(resp, 'data', {}), ['ID:id']),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddUpdateSpouse',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: get(error, 'response.data.error', DEFAULT_ERROR) }],
    };
  }
};

export const msRemoveChildSpouse = async (_, reqData) => {
  try {
    await userService().delete(
      ...USER_P3.removeChildSpouse({
        data: {
          sibling_ids: get(reqData, 'siblings') || [],
          spouse_parent_id: get(reqData, 'IDSpouse'),
        },
      })
    );
    return {
      success: true,
      data: {
        removeChildSpouse: true,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRemoveChildSpouse',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: get(error, 'response.data.error', DEFAULT_ERROR) }],
    };
  }
};

export const getAddressesByCentreIds = (centreIds = []) => {
  if (centreIds?.length === 0) return {};
  return userService()
    .get(...CENTRE.getAddressesByCentreIds(centreIds))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getUserByAcknowledgeById = async acknowledgeBy => {
  if (!acknowledgeBy) return Promise.resolve({});
  return userService()
    .post(
      ...USER_P3.userByTcAcknowledgeBy({ tc_acknowledge_by: acknowledgeBy })
    )
    .then(r => r.data)
    .catch(() => ({}));
};

export const getUserAccessControls = userId => {
  if (!userId) return {};
  return uacService()
    .get(UAC.getMeAccessControlByUserId(userId))
    .then(r => ({
      data: get(r, 'data.data', []),
      totalCount: get(r, 'data.totalCount', 0),
    }))
    .catch(() => ({ data: [], totalCount: 0 }));
};

export const getUserRoleRelationsByUserId = userId => {
  if (!userId) return {};
  return uacService()
    .get(UAC.getUserRoleRelationsByUserId(userId))
    .then(r => ({
      data: get(r, 'data.data', []),
      totalCount: get(r, 'data.totalCount', 0),
    }))
    .catch(() => ({ data: [], totalCount: 0 }));
};

export const getRolesByIds = ids => {
  if (!ids?.length) return {};
  return uacService()
    .post(...UAC.getRolesByIds(ids))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getRoleTemplatesByRoleId = roleId => {
  if (!roleId) return {};
  return uacService()
    .get(UAC.getRoleTemplatesByRoleId(roleId))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getParentByUserId = userId => {
  if (!userId) return {};
  return userService()
    .get(USER_P3.getParentByUserId(userId))
    .then(r => get(r, 'data.data[0]') || {})
    .catch(() => ({}));
};

export const getMe = async () => {
  const [me, accessScope] = await Promise.all([
    userService()
      .get(USER_P3.me)
      .then(r => get(r, 'data', {}))
      .catch(() => ({})),
    uacService()
      .get(UAC.getMeAccessScope)
      .then(r => get(r, 'data', {}))
      .catch(() => ({})),
  ]);

  return {
    ...me,
    ...accessScope,
  };
};

export const getUserAccessControlByUsers = userIds => {
  if (!userIds?.length) return {};
  return uacService()
    .post(UAC.getUserAccessControlByUsers, { user_ids: userIds })
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getStaffUserByStaffIds = staffIds => {
  if (!staffIds?.length) return {};
  return userService()
    .post(...USER_P3.getStaffUserByStaffIds(staffIds))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const updateParentNationality = async data => {
  try {
    const res = await userService().post(USER_P3.updateParentNationality, {
      effective_from: moment(data.effectiveFrom).format(DATE_PICKER_FORMAT),
      nationality: data.nationality?.replace('@enum:', ''),
      identification_no: data.identificationNo,
      parent_id: data.IDParent,
    });
    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    return {
      success: false,
      error: { message: get(error, 'response.data.error', DEFAULT_ERROR) },
    };
  }
};

export const updateParentMaritalStatus = async data => {
  try {
    const res = await userService().post(USER_P3.updateParentMaritalStatus, {
      effective_from: moment(data.effectiveFrom).format(DATE_PICKER_FORMAT),
      marital_status: data.maritalStatus?.replace('@enum:', ''),
      parent_id: data.IDParent,
    });
    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    return {
      success: false,
      error: { message: get(error, 'response.data.error', DEFAULT_ERROR) },
    };
  }
};

export const updateParentWorkingStatus = async data => {
  try {
    const { parentWorkingStatus = {} } = data;
    const res = await userService().post(USER_P3.updateParentWorkingStatus, {
      effective_from: moment(data.effectiveFrom).format(DATE_PICKER_FORMAT),
      parent_id: data.IDParent,
      parent_working_status: {
        commencement_date: moment(parentWorkingStatus.CommencementDate).format(
          DATE_PICKER_FORMAT
        ),
        has_cpf: parentWorkingStatus.HasCpf,
        has_noa: parentWorkingStatus.HasNoa,
        is_employed: parentWorkingStatus.IsEmployed,
        is_recently_employed: parentWorkingStatus.IsRecentlyEmployed,
        monthly_income: parentWorkingStatus.MonthlyIncome,
        occupation_remarks: parentWorkingStatus.OccupationRemarks,
        occupation_title: parentWorkingStatus.OccupationTitle,
        self_employed_monthly_income:
          parentWorkingStatus.SelfEmployedMonthlyIncome,
        working_status: parentWorkingStatus.WorkingStatus?.replace(
          '@enum:',
          ''
        ),
      },
    });
    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    return {
      success: false,
      error: { message: get(error, 'response.data.error', DEFAULT_ERROR) },
    };
  }
};

export const updateParentName = async data => {
  try {
    const res = await userService().post(USER_P3.updateParentName, {
      parent_name: data.fullName,
      parent_id: data.IDParent,
    });
    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    return {
      success: false,
      error: { message: get(error, 'response.data.error', DEFAULT_ERROR) },
    };
  }
};

export const updateParentIdentificationNo = async data => {
  try {
    const res = await userService().post(USER_P3.updateParentIdentificationNo, {
      nationality: data.nationality?.replace('@enum:', ''),
      parent_id: data.IDParent,
      identification_no: data.identificationNo,
    });
    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    return {
      success: false,
      error: { message: get(error, 'response.data.error', DEFAULT_ERROR) },
    };
  }
};

export const addOrUpdateParentAddress = async data => {
  try {
    const { address = {}, IDParent } = data;
    const res = await userService().post(USER_P3.addOrUpdateParentAddress, {
      address: address.address,
      building: address.building,
      fk_user: address.fkUser,
      floor: address.floor,
      name: address.name,
      postcode: address.postcode,
      unit: address.unit,
      fk_parent: IDParent,
    });
    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    return {
      success: false,
      error: { message: get(error, 'response.data.error', DEFAULT_ERROR) },
    };
  }
};

export const updateParentBirthdate = async data => {
  try {
    const res = await userService().post(USER_P3.updateParentBirthdate, {
      birthdate: data.birthdate,
      parent_id: data.IDParent,
    });
    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    return {
      success: false,
      error: { message: get(error, 'response.data.error', DEFAULT_ERROR) },
    };
  }
};

export const updateParentRelation = async data => {
  try {
    const res = await userService().post(USER_P3.updateParentRelation, {
      child_id: data.IDChild,
      relation_code_id: data.IDRelationCode,
      parent_id: data.IDParent,
    });
    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    return {
      success: false,
      error: { message: get(error, 'response.data.error', DEFAULT_ERROR) },
    };
  }
};

export const updateParentEmailOrMobile = async data => {
  try {
    const { IDParent, parentUpdate } = data;
    const [endpoint, body] = USER_P3.updateParentEmailOrMobile({
      parent_id: IDParent,
      ...(parentUpdate?.email !== undefined && {
        email: parentUpdate.email,
      }),
      ...(parentUpdate?.mobilePhone !== undefined && {
        mobile_phone: parentUpdate.mobilePhone,
      }),
      ...(parentUpdate?.mobilePhoneCountryCode !== undefined && {
        mobile_phone_country_code: parentUpdate.mobilePhoneCountryCode,
      }),
    });
    const res = await userService().post(endpoint, body);
    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'updateParentEmailOrMobile',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: { message: get(error, 'response.data.error', DEFAULT_ERROR) },
    };
  }
};

export const msSaveParentParticulars = async (_, data) => {
  const allApiPromises = [];

  if (get(data, 'nationalityData', false)) {
    allApiPromises.push(updateParentNationality(data.nationalityData));
  }

  if (get(data, 'maritialData', false)) {
    allApiPromises.push(updateParentMaritalStatus(data.maritialData));
  }

  if (get(data, 'workingStatusData', false)) {
    allApiPromises.push(updateParentWorkingStatus(data.workingStatusData));
  }

  if (get(data, 'parentNameData', false)) {
    allApiPromises.push(updateParentName(data.parentNameData));
  }

  if (get(data, 'parentIdentificationNoData', false)) {
    allApiPromises.push(
      updateParentIdentificationNo(data.parentIdentificationNoData)
    );
  }

  if (get(data, 'updateEmailMobile', false)) {
    allApiPromises.push(updateParentEmailOrMobile(data.updateEmailMobile));
  }

  if (get(data, 'parentAddressData', false)) {
    allApiPromises.push(addOrUpdateParentAddress(data.parentAddressData));
  }

  if (get(data, 'parentDobData', false)) {
    allApiPromises.push(updateParentBirthdate(data.parentDobData));
  }

  if (get(data, 'parentRelationData', false)) {
    allApiPromises.push(updateParentRelation(data.parentRelationData));
  }

  try {
    const res = await Promise.all(allApiPromises);
    const isSuccess = res.every(r => r.success);
    if (isSuccess) {
      return {
        success: true,
        data: res,
      };
    }
    return { success: false, error: (res || []).map(r => r.error) };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSaveParentParticulars',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const getAddressesByUserIds = (userIDs = []) => {
  if (userIDs.length === 0) {
    return [];
  }
  return userService()
    .post(...USER_P3.getAddressesByUserIds(userIDs))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getRolesByUserIds = (userIDs = [], params = {}) => {
  if (userIDs.length === 0) {
    return {};
  }
  return uacService()
    .post(UAC.getRolesByUserIds, { user_ids: userIDs }, { params })
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const msFetchExistingParentInfo = async (_, reqData) => {
  try {
    const { parentFilter, childId } = reqData;
    const res = await userService().post(USER_P3.getParentByFilter, {
      identification_no: [parentFilter?.identificationNo],
      active: [true, false],
    });
    const userId = get(res, 'data.data[0].fk_user');
    const parentId = get(res, 'data.data[0].id');

    const [childRelations, parentRelation] = await Promise.all([
      getChildRelationsByUserIds([userId]),
      msGetChildRelationFromParentId(parentId),
    ]);
    const childRelationData = get(childRelations, `${userId}.data`, []);
    const childRelationDetails = childRelationData.find(
      cr => cr.fk_child === childId
    );
    return {
      success: true,
      data: {
        getParent: {
          data: get(res, 'data.data', []).map(parent => {
            return {
              ...convertObjToCamelCase(parent, []),
              userByFkUser: {
                ID: get(parent, 'fkUser'),
              },
              childRelations: childRelationDetails?.id
                ? {
                    data: [
                      {
                        fkRelation: get(childRelationDetails, 'fk_relation'),
                        fkChild: get(childRelationDetails, 'fk_child'),
                      },
                    ],
                  }
                : null,
              relation: get(parentRelation, 'relation', ''),
            };
          }),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchExistingParentInfo',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msFetchAllStaff = async (_, reqData, isFindAllStaffV2 = false) => {
  try {
    const { role, centre, searchString, pagination, filter } = reqData;

    let roleIds = null;
    if (role) {
      roleIds = isArray(role) ? role : [role];
    }

    const keyField = isFindAllStaffV2 ? 'findAllStaffV2' : 'findAllStaff';

    const res = filter?.fkUser
      ? await userService().get(USER_P3.getStaffByUserId(filter?.fkUser))
      : await userService().post(
          USER_P3.findAllStaff,
          {
            centre_id: centre,
            role_ids: roleIds,
            search_string: searchString,
            staff_filter: {
              staff_ids: get(reqData, 'filter.staffID')
                ? [get(reqData, 'filter.staffID')]
                : [],
            },
          },
          {
            params: {
              page: pagination?.page || 1,
              perPage: pagination?.perPage || DEFAULT_ITEMS_PER_PAGE,
            },
          }
        );

    const allStaff = filter?.fkUser ? [res.data] : res.data?.data || [];
    const userIds = allStaff.map(staff => staff.fk_user);
    const updatedUserIds = allStaff.map(staff => staff.last_modified_user);
    const [userRoles, users, userAccessControls] = await Promise.all([
      getRolesByUserIds(userIds, {
        page: 1,
        perPage: MAX_PER_PAGE,
      }),
      getUserByIds(uniq([...userIds, ...updatedUserIds])),
      getUserAccessControlByUsers(userIds),
    ]);
    const centreIds = Object.values(userAccessControls)
      .flat()
      .map(item => item.fk_centre)
      .filter(Boolean);
    const userMap = convertObjToCamelCase(keyBy(users, 'id'));
    const centres = await getCentreAddressByIds(uniq(centreIds));
    const centreMap = convertObjToCamelCase(keyBy(centres?.data, 'id'));

    return {
      data: {
        [keyField]: {
          data: allStaff.map(staff => ({
            ...convertObjToCamelCase(staff, ['staffID:staff_id']),
            userByFkUser: {
              ...userMap[staff.fk_user],
              userRoleRelations: {
                data: [
                  {
                    role: convertObjToCamelCase(userRoles[staff.fk_user]),
                  },
                ],
              },
              userAccessControls: {
                data: userAccessControls[staff.fk_user].map(item => ({
                  centre: centreMap[item.fk_centre],
                })),
              },
            },
            userByLastModifiedUser: userMap[staff.last_modified_user],
          })),
          totalCount: res.data?.totalCount,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAllStaff ',
        fileName,
      },
      'error'
    );
    return {
      data: {
        findAllStaff: {
          data: [],
        },
      },
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAllStaffV2 = async (dispatch, reqData) => {
  return msFetchAllStaff(dispatch, reqData, true);
};

export const msLinkpassUserInfoFromClaim = async (_, reqData) => {
  try {
    const reqBody = {
      link_pass_id_token: reqData?.linkPassIdToken,
    };
    const resp = await userService().post(
      USER_P3.getLinkPassUserInfoFromClaim,
      reqBody
    );
    const responseData = get(resp, 'data', {});
    const linkPassAuthTokenClaimDto = get(
      responseData,
      'LinkPassAuthTokenClaimDto',
      {}
    );
    const user = get(responseData, 'User', {});
    const linkPassCompareWithUser = get(
      responseData,
      'LinkPassCompareWithUser',
      {}
    );

    return {
      data: {
        linkpassUserInfoFromClaim: {
          LinkPassAuthTokenClaimDto: {
            UID: get(linkPassAuthTokenClaimDto, 'UID', ''),
            Email: get(linkPassAuthTokenClaimDto, 'Email', ''),
            CountryCode: get(linkPassAuthTokenClaimDto, 'CountryCode', ''),
            PhoneNumber: get(linkPassAuthTokenClaimDto, 'PhoneNumber', ''),
          },
          User: {
            ID: get(user, 'id', ''),
            email: get(user, 'email', ''),
            mobilePhoneCountryCode: get(user, 'mobile_phone_country_code', ''),
            mobilePhone: get(user, 'mobile_phone', ''),
          },
          LinkPassCompareWithUser: {
            IsDiffEmail: get(linkPassCompareWithUser, 'IsDiffEmail', false),
            IsDiffPhone: get(linkPassCompareWithUser, 'IsDiffPhone', false),
          },
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msLinkpassUserInfoFromClaim',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msLinkpassSn2UserInfo = async (_, reqData) => {
  try {
    const body = {
      email: reqData?.email,
      password: reqData?.password,
      link_pass_id_token: reqData?.linkPassIdToken,
    };
    const resp = await userService().post(
      ...USER_P3.getLinkpassSn2UserInfo(body)
    );
    const responseData = get(resp, 'data', {});

    return {
      success: true,
      data: transformLinkpassSn2UserInfoResponse(responseData),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msLinkpassSn2UserInfo',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msLinkUserWithLinkPassAndLogin = async (_, reqData) => {
  const { email, password, linkPassIdToken } = reqData;
  try {
    const body = {
      email,
      password,
      link_pass_id_token: linkPassIdToken,
    };
    const resp = await userService().post(
      ...USER_P3.linkUserWithLinkPassLogin,
      body
    );
    const token = get(resp, 'data.token', '');
    return {
      data: {
        linkUserWithLinkPassAndLogin: token,
      },
      success: !!get(resp, 'data.token'),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msLinkUserWithLinkPassAndLogin',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msRegisterWithLinkPassInfo = async (_, reqData) => {
  try {
    const { registerToken, linkPassIdToken } = reqData;
    const body = {
      link_pass_id_token: linkPassIdToken,
      token: registerToken,
    };
    const resp = await userService().post(
      ...USER_P3.registerQithLinkPassInfo(body)
    );
    const responseData = get(resp, 'data', {});
    const transformedData = transformRegisterWithLinkPassInfo(responseData);
    return transformedData;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRegisterWithLinkPassInfo',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msValidateUser = async (_, tokenString) => {
  try {
    // TODO: Replace with actual MS endpoint when available
    // Mock implementation for now
    // Simulating MS response structure
    const mockResponse = {
      success: true,
      data: {
        validateUser: {
          isValid: !!tokenString,
          email: 'mock@example.com',
          message: `Token is valid (Mock MS Response) - Token: ${tokenString?.substring(
            0,
            10
          )}...`,
        },
      },
    };

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 100));

    return mockResponse;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        fileName,
        functionName: 'msValidateUser',
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: get(ex, 'message', 'Validation failed') }],
    };
  }
};

export const msUpdateElectronicConsentStatus = async (
  _,
  { tokenString, consentStatus }
) => {
  try {
    // TODO: Replace with actual MS endpoint when available
    // Mock implementation for now
    // Simulating MS response structure
    const mockResponse = {
      success: true,
      data: {
        updateElectronicConsentStatus: {
          success: true,
          tokenString,
          consentStatus,
          message: `Electronic consent status updated to: ${consentStatus} (Mock MS Response)`,
          updatedAt: moment().format(DATE_PICKER_FORMAT),
        },
      },
    };

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 150));

    return mockResponse;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        fileName,
        functionName: 'msUpdateElectronicConsentStatus',
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'message',
            'Failed to update electronic consent status'
          ),
        },
      ],
    };
  }
};
